import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

const navLinks = [
  { path: '/services', label: 'services' },
  { path: '/about', label: 'about' },
  { path: '/contact', label: 'contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || menuOpen ? 'bg-black/90 backdrop-blur-lg border-b border-outline' : 'bg-transparent'
        }`}
      >
        <div className="flex items-center justify-between px-8 md:px-16 lg:px-24 py-6">
          <Link
            to="/"
            className="text-2xl md:text-3xl tracking-tight text-cloud hover:text-soft-white transition-colors duration-500"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            <span className="text-silver font-medium">[</span>
            <span className="font-light">data</span>
            <span className="text-silver font-medium">]</span>
            <span className="font-medium"> kitchen</span>
          </Link>

          <div className="flex items-center space-x-8">
            {navLinks.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`hidden md:block text-sm transition-colors pb-0.5 ${
                  isActive(path)
                    ? 'text-cloud border-b border-cloud'
                    : 'text-silver hover:text-soft-white border-b border-transparent'
                }`}
              >
                {label}
              </Link>
            ))}

            <Link to="/contact" className="hidden md:block">
              <Button
                variant="outline"
                size="sm"
                className="border-cloud text-cloud hover:bg-cloud hover:text-black px-6 py-2 text-xs tracking-wider uppercase transition-all duration-300"
              >
                Book a Call
              </Button>
            </Link>

            <button
              className="md:hidden text-cloud p-1"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-40 bg-void flex flex-col justify-center px-10"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            <nav className="space-y-8">
              {navLinks.map(({ path, label }, i) => (
                <motion.div
                  key={path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.07, duration: 0.3 }}
                >
                  <Link
                    to={path}
                    className={`block text-4xl font-light tracking-tight transition-colors ${
                      isActive(path) ? 'text-cloud' : 'text-silver hover:text-cloud'
                    }`}
                  >
                    {label}
                  </Link>
                </motion.div>
              ))}
            </nav>

            <motion.div
              className="mt-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-cloud text-cloud hover:bg-cloud hover:text-black px-8 py-6 text-sm tracking-wider uppercase transition-all duration-300"
                >
                  Book a Call
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
